import type { CalloutBlock } from "../contract/types";
import { Icon, type IconName } from "../components/Icon";
import { RichText } from "../components/RichText";
import type { BlockViewProps } from "./types";

/**
 * Destacado. Seis variantes, cada una con su icono y —cuando el color solo no
 * basta— su etiqueta escrita.
 *
 * `outside-syllabus` es la más importante del curso: marca contenido que va
 * más allá del sílabo. Lleva un badge visible "Fuera del sílabo" y un borde
 * discontinuo, para que nadie confunda una ampliación con materia evaluable.
 */

type Variant = NonNullable<CalloutBlock["variant"]>;

interface VariantStyle {
  icon: IconName;
  /** Etiqueta que se muestra cuando el autor no puso título. */
  defaultLabel: string;
  /** El badge se pinta siempre, aunque haya título propio. */
  badge?: string;
}

const VARIANTS: Record<Variant, VariantStyle> = {
  "key-idea": { icon: "lightbulb", defaultLabel: "Idea clave" },
  warning: { icon: "alert", defaultLabel: "Atención" },
  "common-error": { icon: "x", defaultLabel: "Error frecuente" },
  tip: { icon: "sparkle", defaultLabel: "Sugerencia" },
  note: { icon: "info", defaultLabel: "Nota" },
  "outside-syllabus": {
    icon: "compass",
    defaultLabel: "Ampliación",
    badge: "Fuera del sílabo",
  },
};

/**
 * El cuerpo se renderiza como markdown enriquecido, no como texto plano.
 *
 * El modelo escribe markdown y LaTeX aquí de forma natural —`**gastos
 * financieros**`, `$k_d$`— y tratarlo como texto literal dejaba esos signos a
 * la vista. Es el mismo render que usa `prose`, así que el mismo fragmento se
 * ve igual en los dos átomos.
 */
function renderBody(body: string) {
  return <RichText className="callout__rich">{body}</RichText>;
}

export function Callout({ block }: BlockViewProps<CalloutBlock>) {
  const variant: Variant = block.variant ?? "note";
  const style = VARIANTS[variant] ?? VARIANTS.note;
  const title = block.title ?? style.defaultLabel;

  return (
    <div className={`atom atom--callout callout callout--${variant}`}>
      <div className="callout__icon" aria-hidden="true">
        <Icon name={style.icon} size="1.125rem" />
      </div>
      <div className="callout__body">
        <div className="callout__head">
          <h3 className="callout__title">{title}</h3>
          {style.badge ? <span className="callout__badge">{style.badge}</span> : null}
        </div>
        <div className="callout__content">{renderBody(block.body)}</div>
      </div>
    </div>
  );
}

export default Callout;
